import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../stores/useAuthStore';
import { apiClient } from '../api/client';
import { Button } from '../components/Button';
import { Input } from '../components/Input';
import { Card, CardHeader, CardContent, CardFooter } from '../components/Card';
import { Lock, User, ChevronRight } from 'lucide-react';
import { socketService } from '../api/socket';
import { useTranslation } from '../../i18n/LanguageContext';

import { queryClient } from '../providers/QueryProvider';

export const LoginPage: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { setAuth } = useAuthStore();
    const { t } = useTranslation();

    const [employeeId, setEmployeeId] = useState('');
    const [password, setPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get('reason') === 'session_conflict') {
            return t('auth.sessionConflictMessage') || 'You were logged out because another device signed in with your account.';
        }
        return '';
    });
    const [showSessionConfirm, setShowSessionConfirm] = useState(false);

    const handleLogin = async (e?: React.FormEvent, force = false) => {
        if (e) e.preventDefault();
        setIsLoading(true);
        setError('');

        // Clear previous state just in case
        queryClient.clear();

        try {
            const response = await apiClient.post('/auth/login', {
                employeeId: parseInt(employeeId),
                password,
                force
            });

            const { user } = response.data;

            // Update store (tokens are in cookies now)
            setAuth(user);

            // Fetch dedicated socket token
            const socketTokenResponse = await apiClient.get('/auth/socket-token');
            const { socketToken } = socketTokenResponse.data;

            // Connect socket
            let namespace: '/staff' | '/admin' | '/customer' = '/staff';
            if (user.role === 'SUPER_ADMIN') namespace = '/admin';

            socketService.connect(socketToken, namespace);

            // Redirect based on role or previous location
            const from = (location.state as any)?.from?.pathname;
            const role = user.role.toLowerCase().replace('_', '-');
            const dashboardPath = `/${role === 'super-admin' ? 'admin' : role === 'branch-manager' ? 'manager' : role}`;

            // Only use 'from' if it's compatible with the user's dashboard or a generic path
            if (from && (from === '/' || from.startsWith(dashboardPath))) {
                navigate(from, { replace: true });
                return;
            }

            // Fallback to role-based dashboard
            navigate(dashboardPath, { replace: true });
        } catch (err: any) {
            const status = err.response?.status;
            const errorData = err.response?.data?.error || err.response?.data;
            const errorCode = errorData?.code;
            const errorMessage = typeof errorData === 'object' ? errorData.message : errorData;

            // Robust check for session conflict
            const isSessionConflict =
                (status === 409 && (errorCode === 'CONFLICT' || errorMessage === 'ACTIVE_SESSION_EXISTS')) ||
                errorMessage === 'ACTIVE_SESSION_EXISTS';

            if (isSessionConflict) {
                setShowSessionConfirm(true);
            } else {
                // Detailed logging for actual errors
                console.error('Login failed', {
                    status,
                    errorCode,
                    errorMessage,
                    message: err.message,
                    stack: err.stack,
                    data: err.response?.data
                });

                const displayMessage = errorMessage || err.message || 'Invalid credentials. Please try again.';
                setError(displayMessage);
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center p-4 bg-[url('https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?q=80&w=2670&auto=format&fit=crop')] bg-cover bg-center">
            {/* Overlay */}
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm"></div>

            <Card className="w-full max-w-md relative z-10 animate-fade-in border-none shadow-2xl">
                <CardHeader className="text-center pb-2">
                    <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4 text-primary">
                        <Lock size={32} />
                    </div>
                    <h1 className="text-2xl font-serif font-bold text-gray-900">{t('auth.welcomeBack')}</h1>
                    <p className="text-gray-500 text-sm mt-1">{t('auth.signInToContinue')}</p>
                </CardHeader>

                <form onSubmit={handleLogin}>
                    <CardContent className="space-y-4">
                        {error && (
                            <div className="p-3 rounded-md bg-destructive/10 border border-destructive/20 text-destructive text-sm font-medium">
                                {error}
                            </div>
                        )}

                        <Input
                            id="employeeId"
                            name="employeeId"
                            label={t('auth.employeeId')}
                            type="number"
                            placeholder={t('auth.enterEmployeeId')}
                            value={employeeId}
                            onChange={(e) => setEmployeeId(e.target.value)}
                            leftIcon={<User size={18} />}
                            required
                            autoFocus
                            autoComplete="username"
                        />

                        <Input
                            id="password"
                            name="password"
                            label={t('auth.password')}
                            type="password"
                            placeholder="••••••••"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            leftIcon={<Lock size={18} />}
                            required
                            autoComplete="current-password"
                        />

                        <div className="flex justify-end">
                            <button type="button" className="text-xs font-medium text-primary hover:underline">
                                {t('auth.forgotPassword')}
                            </button>
                        </div>
                    </CardContent>

                    <CardFooter>
                        <Button
                            type="submit"
                            className="w-full"
                            size="lg"
                            isLoading={isLoading}
                            rightIcon={!isLoading && <ChevronRight size={18} className="rtl-flip" />}
                        >
                            {t('auth.login')}
                        </Button>
                    </CardFooter>
                </form>
            </Card>

            {/* Session Confirmation Modal */}
            {showSessionConfirm && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
                    <Card className="w-full max-w-sm border-none shadow-2xl animate-scale-in">
                        <CardHeader className="text-center">
                            <div className="mx-auto w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mb-4 text-amber-600">
                                <Lock size={24} />
                            </div>
                            <h2 className="text-xl font-bold text-gray-900">{t('auth.sessionConfirmTitle')}</h2>
                        </CardHeader>
                        <CardContent>
                            <p className="text-gray-600 text-sm leading-relaxed text-center">
                                {t('auth.sessionConfirmMessage')}
                            </p>
                        </CardContent>
                        <CardFooter className="flex flex-col gap-2 pt-2">
                            <Button
                                className="w-full bg-amber-600 hover:bg-amber-700 border-none"
                                onClick={() => {
                                    setShowSessionConfirm(false);
                                    handleLogin(undefined, true);
                                }}
                                isLoading={isLoading}
                            >
                                {t('auth.loginAnyway')}
                            </Button>
                            <Button
                                variant="outline"
                                className="w-full"
                                onClick={() => setShowSessionConfirm(false)}
                                disabled={isLoading}
                            >
                                {t('common.cancel')}
                            </Button>
                        </CardFooter>
                    </Card>
                </div>
            )}

            <div className="absolute bottom-6 text-white/50 text-xs text-center z-10">
                &copy; {new Date().getFullYear()} {t('common.adminTitle')} {t('common.managementSystem')}
            </div>
        </div>
    );
};
